//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useEffect, useState } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  SamplingDeliveryMethod,
  SamplingOpportunitiesSortOrder,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
} from '@apis/campaign-requests/shared/Pagination';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { ChangePurchaseOptionConfirmation } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionConfirmation';
import {
  ChangePurchaseOptionModalProvider,
  ChangePurchaseOptionModalStep,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalContext';
import { ChangePurchaseOptionModalTable } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalTable';
import { PrepaidLocationModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PrepaidLocationModal';
import { CampaignModalContentSetup } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignModalContentSetup';
import { ExpiredPurchaseOptionsTable } from '@campaigns/components/shared/prepaid-purchase-options/ExpiredPurchaseOptionsTable';
import { getSortParam } from '@campaigns/components/shared/prepaid-purchase-options/PrepaidPurchaseOptionsSortUtil';
import { usePrepaidPurchaseOptions } from '@campaigns/components/shared/purchase-options/usePrepaidPurchaseOptions';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import {
  PurchaseOptionsSortKey,
  UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE,
  USER_ERROR_MESSAGES,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { getIsSamplingContentDestinationsEnabled } from '@campaigns/redux/selectors/getIsSamplingContentDestinationsEnabled';
import { isSingleRetailerPurchaseOption } from '@campaigns/utils/sampling/PrepaidCampaignUtils';
import {
  getFulfillmentOptionsFromPurchaseOption,
  getMemberTasksFromPurchaseOption,
} from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import { isPurchaseOptionExpiredOrZeroUnits } from '@campaigns/utils/sampling/SamplingPrepaidValidationUtils';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { Modal } from '@components/molecules/modal/Modal';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { pluralize } from '@utils/stringUtils';

interface ChangePurchaseOptionModalProps {
  headerText: string;
  isLoading?: boolean;
  isModalOpen: boolean;
  modalTitle: string;
  onClose: () => void;
  onCreateNewPurchase?: () => void;
  shouldIncludeTertiaryButton?: boolean;
}

export const ChangePurchaseOptionModal = ({
  headerText,
  isLoading = false,
  isModalOpen,
  modalTitle,
  onClose,
  onCreateNewPurchase,
  shouldIncludeTertiaryButton = false,
}: ChangePurchaseOptionModalProps) => {
  const mainFormMethods = useFormContext<SamplingCampaignRequest>();
  const localFormMethods = useForm<SamplingCampaignRequest>({
    defaultValues: mainFormMethods.getValues(),
    mode: 'onChange',
  });

  const {
    control,
    formState: { defaultValues },
    getValues,
    reset: resetLocalForm,
    setValue,
  } = localFormMethods;

  const { getValues: getMainValues, reset: resetMainForm } = mainFormMethods;

  useEffect(() => {
    if (isModalOpen) {
      resetLocalForm(getMainValues());
    }
  }, [getMainValues, isModalOpen, resetLocalForm]);

  const selectedOptions =
    useWatch({
      control,
      name: 'purchaseOption',
    }) || [];
  const selectedOptionId = selectedOptions[0]?.opportunityId;

  const { handleSave, isSaving } = useSamplingCampaign();

  const isSamplingContentDestinationsEnabled = useSelector(
    getIsSamplingContentDestinationsEnabled,
  );
  const [step, setStep] = useState<ChangePurchaseOptionModalStep>(
    ChangePurchaseOptionModalStep.PURCHASE_OPTIONS,
  );
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<PurchaseOptionsSortKey>(
    PurchaseOptionsSortKey.EXPIRY_DATE,
  );
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.ASC);

  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  const {
    error,
    isPending,
    purchaseOptions,
    total: totalItemsResponse,
  } = usePrepaidPurchaseOptions({
    request: {
      excludeZeroBalance: true,
      limit: itemsPerPage,
      offset: currentPage * itemsPerPage,
      searchProperty: debouncedSearchTerm,
      sortBy: getSortParam(sortKey),
      sortOrder:
        sortDir === SortDir.ASC
          ? SamplingOpportunitiesSortOrder.ascending
          : SamplingOpportunitiesSortOrder.descending,
    },
    selectedOptions,
  });

  const totalItems = totalItemsResponse ?? 0;
  const resultsText = pluralize(totalItems, 'option');

  const handleSortChange = (key: PurchaseOptionsSortKey) => {
    if (key === sortKey) {
      setSortDir((prevSortDir) =>
        prevSortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
      );
    } else {
      setSortKey(key);
      setSortDir(SortDir.ASC);
    }
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleSelectionChange = useCallback(
    (optionId: string) => {
      const option = purchaseOptions.find((p) => p.opportunityId === optionId);
      if (option) {
        setValue('purchaseOption', [option], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [purchaseOptions, setValue],
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleClose = () => {
    setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    onClose();
  };

  const handleGoBackToCampaigns = () => {
    void navigateToState('campaigns.sampling');
  };

  const getConfirmationContext = () => {
    const selectedPurchaseOption = selectedOptions[0];
    const { fulfillmentType, whoShipsProduct } =
      getValues('fulfillmentOptions') || {};
    const targetDeliveryMethod = selectedPurchaseOption?.deliveryMethod;

    const isSourceShippedByBV =
      fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';
    const isSourceDeals = fulfillmentType === 'deals';
    const isSourceShippedByYou =
      fulfillmentType === 'shipped' && whoShipsProduct === 'myCompany';

    const isTargetDigital =
      targetDeliveryMethod === SamplingDeliveryMethod.DigitalDelivery;
    const isTargetShippedByYou =
      targetDeliveryMethod === SamplingDeliveryMethod.Shipped;
    const isTargetShippedByBV =
      targetDeliveryMethod === SamplingDeliveryMethod.ShippedByBazaarvoice;

    interface TransitionRule {
      condition: boolean;
      removedSection?: SamplingCampaignRequestSectionName;
    }

    const transitionRules: TransitionRule[] = [
      {
        condition:
          isSourceShippedByBV && (isTargetDigital || isTargetShippedByYou),
        removedSection: SamplingCampaignRequestSectionName.PACKAGING,
      },
      {
        condition:
          isSourceDeals && (isTargetShippedByYou || isTargetShippedByBV),
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetDigital,
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetShippedByBV,
        removedSection: undefined,
      },
    ];

    const matchedRule = transitionRules.find(({ condition }) => condition);

    return {
      fulfillmentType,
      removedDataSection: matchedRule?.removedSection,
      shouldResetFulfillment: !!matchedRule,
      targetCampaignType: targetDeliveryMethod,
    };
  };

  const updateFormValuesForSetup = () => {
    const data = getValues();
    const selectedOption = data.purchaseOption?.[0];
    const savedOption = defaultValues?.purchaseOption?.[0];

    if (
      selectedOption &&
      selectedOption.opportunityId !== savedOption?.opportunityId
    ) {
      const currentFulfillmentOptions = getValues('fulfillmentOptions') || {};
      const { shouldResetFulfillment } = getConfirmationContext();
      const baseOptions = shouldResetFulfillment
        ? {}
        : currentFulfillmentOptions;

      if (selectedOption.deliveryMethod) {
        setValue('fulfillmentOptions', {
          ...baseOptions,
          ...getFulfillmentOptionsFromPurchaseOption(selectedOption),
        });
      }

      if (selectedOption.contentType) {
        const { displayReviews, memberTasks } =
          getMemberTasksFromPurchaseOption(selectedOption);
        setValue('memberTasks', memberTasks);
        setValue('displayReviews', displayReviews);
      }

      setValue('isNewPurchase', false);
    }
  };

  const handleFormSubmit = async (data: SamplingCampaignRequest) => {
    try {
      setSubmissionError(null);
      resetMainForm(data);
      await handleSave(false);
      const campaignId = data.campaignRequestId;
      if (campaignId) {
        void navigateToState(
          'campaigns.sampling-prepaid-campaign-request-details-edit-products',
          {
            campaignId,
          },
        );
      }
      handleClose();
    } catch (e) {
      setSubmissionError(
        e instanceof Error ? e.message : USER_ERROR_MESSAGES.DEFAULT,
      );
    }
  };

  const isUpdateDisabled = !selectedOptionId;

  const handleNextOrSubmit = async () => {
    if (step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS) {
      if (selectedOptionId) {
        setStep(ChangePurchaseOptionModalStep.CONFIRM);
      }
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      updateFormValuesForSetup();
      // This will need changed once multiple purchase options are supported in prepaid
      const selectedOption = selectedOptions[0];
      if (
        isSamplingContentDestinationsEnabled &&
        selectedOption &&
        !isSingleRetailerPurchaseOption(selectedOption)
      ) {
        setStep(ChangePurchaseOptionModalStep.LOCATION);
      } else {
        setStep(ChangePurchaseOptionModalStep.SETUP);
      }
    } else if (
      isSamplingContentDestinationsEnabled &&
      step === ChangePurchaseOptionModalStep.LOCATION
    ) {
      await localFormMethods.handleSubmit(handleFormSubmit)();
    } else {
      await localFormMethods.handleSubmit(handleFormSubmit)();
    }
  };

  const handleBack = () => {
    if (step === ChangePurchaseOptionModalStep.LOCATION) {
      setStep(ChangePurchaseOptionModalStep.CONFIRM);
    } else if (step === ChangePurchaseOptionModalStep.SETUP) {
      setStep(ChangePurchaseOptionModalStep.CONFIRM);
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    } else {
      handleClose();
    }
  };

  const isPurchaseOptionsStep =
    step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS;
  const isConfirmStep = step === ChangePurchaseOptionModalStep.CONFIRM;
  const isLocationStep = step === ChangePurchaseOptionModalStep.LOCATION;

  const savedPurchaseOptionsValues = useWatch({
    control: mainFormMethods.control,
    name: 'purchaseOption',
  });

  const {
    isPending: isCheckingExpired,
    purchaseOptions: availableSavedOptions,
  } = usePrepaidPurchaseOptions({
    request: {
      limit: MAX_PAGE_SIZE,
      offset: 0,
      excludeZeroBalance: false,
    },
    selectedOptions: savedPurchaseOptionsValues,
  });

  useEffect(() => {
    if (isCheckingExpired || savedPurchaseOptionsValues?.length === 0) {
      return;
    }

    const updatedOptions = savedPurchaseOptionsValues?.map((saved) => {
      if (isPurchaseOptionExpiredOrZeroUnits(saved)) {
        const latestOption = availableSavedOptions.find(
          (opp) => opp.opportunityId === saved.opportunityId,
        );

        if (latestOption && !isPurchaseOptionExpiredOrZeroUnits(latestOption)) {
          return latestOption;
        }
      }
      return saved;
    });

    const shouldUpdatePurchaseOption = updatedOptions?.some(
      (updated, index) => {
        const saved = savedPurchaseOptionsValues?.[index];
        if (!saved) {
          return false;
        }
        return (
          isPurchaseOptionExpiredOrZeroUnits(updated) !==
          isPurchaseOptionExpiredOrZeroUnits(saved)
        );
      },
    );

    if (shouldUpdatePurchaseOption) {
      setValue('purchaseOption', updatedOptions);
    }
  }, [
    availableSavedOptions,
    isCheckingExpired,
    savedPurchaseOptionsValues,
    setValue,
  ]);

  const hasExpiredSelectedOption = savedPurchaseOptionsValues?.some((saved) => {
    const latestOption = availableSavedOptions.find(
      (opp) => opp.opportunityId === saved.opportunityId,
    );
    const optionToCheck = latestOption ?? saved;

    return isPurchaseOptionExpiredOrZeroUnits(optionToCheck);
  });

  const expiredOriginalOptions = savedPurchaseOptionsValues
    ?.map(
      (option) =>
        availableSavedOptions.find(
          (original) => original.opportunityId === option.opportunityId,
        ) ?? option,
    )
    .filter(isPurchaseOptionExpiredOrZeroUnits);

  const currentModalTitle = isPurchaseOptionsStep
    ? hasExpiredSelectedOption
      ? UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE
      : modalTitle
    : isConfirmStep
      ? 'Are You Sure?'
      : 'Update Campaign';

  const primaryButtonText = isPurchaseOptionsStep
    ? 'Next'
    : isConfirmStep
      ? 'Next'
      : 'Update Campaign';

  const tertiaryButtonInfo = (() => {
    if (hasExpiredSelectedOption) {
      return {
        buttonContent: 'Go back to Campaigns',
        onClick: handleGoBackToCampaigns,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_BACK_BUTTON,
      };
    }
    if (isSamplingContentDestinationsEnabled && isLocationStep) {
      return {
        buttonContent: 'Cancel',
        onClick: handleClose,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_BACK_BUTTON,
      };
    }
    return undefined;
  })();

  const modalWidth = (() => {
    if (isPurchaseOptionsStep) {
      return 'extra-wide';
    }
    if (isLocationStep) {
      return 'wide';
    }
    return 'medium';
  })();

  const renderContent = () => {
    switch (step) {
      case ChangePurchaseOptionModalStep.PURCHASE_OPTIONS:
        if (isCheckingExpired || isLoading) {
          return <CenteredSpinner />;
        }
        return (
          <>
            {hasExpiredSelectedOption && (
              <ExpiredPurchaseOptionsTable
                expiredSelectedOptions={expiredOriginalOptions ?? []}
              />
            )}
            <ChangePurchaseOptionModalTable
              currentPage={currentPage}
              error={error}
              expiredPurchaseOptionModalTitle={
                hasExpiredSelectedOption ? modalTitle : undefined
              }
              headerText={headerText}
              isLoading={isPending}
              itemsPerPage={itemsPerPage}
              onCreateNewPurchase={onCreateNewPurchase}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              purchaseOptions={purchaseOptions}
              resultsText={resultsText}
              searchTerm={searchTerm}
              selectedOptionId={selectedOptionId}
              setCurrentPage={handlePageChange}
              setItemsPerPage={handleItemsPerPageChange}
              setSearchTerm={handleSearchChange}
              showCreateNewPurchaseButton={!!onCreateNewPurchase}
              sortDir={sortDir}
              sortKey={sortKey}
              totalItems={totalItems}
            />
          </>
        );
      case ChangePurchaseOptionModalStep.CONFIRM: {
        const { fulfillmentType, removedDataSection, targetCampaignType } =
          getConfirmationContext();
        return (
          <ChangePurchaseOptionConfirmation
            fulfillmentType={fulfillmentType}
            removedDataSection={removedDataSection}
            targetCampaignType={targetCampaignType}
          />
        );
      }
      case ChangePurchaseOptionModalStep.LOCATION:
        return (
          <PrepaidLocationModal
            deliveryMethod={selectedOptions[0]?.deliveryMethod}
          />
        );
      case ChangePurchaseOptionModalStep.SETUP:
        return <CampaignModalContentSetup />;
      default:
        throw new Error('Invalid modal step');
    }
  };
  return (
    <ChangePurchaseOptionModalProvider
      value={{
        formMethods: localFormMethods,
        handleFormSubmit,
        isPending: isSaving,
        setStep,
        step,
        submissionError,
      }}
    >
      <FormProvider {...localFormMethods}>
        <Modal
          closeModal={hasExpiredSelectedOption ? undefined : handleClose}
          contents={renderContent()}
          isModalOpen={isModalOpen}
          primaryButtonInfo={{
            buttonContent: primaryButtonText,
            isDisabled: isUpdateDisabled || isSaving || isLoading,
            onClick: handleNextOrSubmit,
            trackingId:
              SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_BUTTON,
          }}
          secondaryButtonInfo={
            !isPurchaseOptionsStep
              ? {
                  buttonContent: 'Back',
                  onClick: handleBack,
                  trackingId:
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_CANCEL_BUTTON,
                }
              : undefined
          }
          shouldCloseOnClickOutside={false}
          shouldCloseOnEsc={false}
          shouldIncludeSecondaryButton={!isPurchaseOptionsStep}
          shouldIncludeTertiaryButton={
            shouldIncludeTertiaryButton ||
            hasExpiredSelectedOption ||
            isLocationStep
          }
          tertiaryButtonInfo={tertiaryButtonInfo}
          title={currentModalTitle}
          width={modalWidth}
        />
      </FormProvider>
    </ChangePurchaseOptionModalProvider>
  );
};
